import { NAME_FILTERS } from 'constants/dataSets';
import { CONNECTED_FILTERS } from 'constants/general';
import { getCountryDetails } from 'utils/country';
import {
  COUNTRY_CODES_FILTER_KEY,
  getFilterIdentifier,
  isCountryCodeFilter,
} from 'utils/valueChainHelpers';

import type IFilterData from 'pages/CompanyAdminPages/CompanyMap/components/ValueChain/interfaces/IFilterData';
import type { IBoardFilter } from 'store/slices/valueChainMap/interfaces/IBoardFilter';
import type { IBoardFilterItem } from 'store/slices/valueChainMap/interfaces/IBoardFilterItem';
import type { TCountryDetailsLookup } from 'utils/country';

export interface IBuildFilterCatalogueParams {
  rawFilters?: IBoardFilter,
  countryDetailsLookup: TCountryDetailsLookup,
  translate: (key: string, fallback: string) => string,
}

// Turns a raw board-filters response into the IFilterData[] catalogue the panel consumes.
// Shared by useFilters (mode-selected source) and useUserFilters (unfiltered source).
const buildFilterCatalogue = ({
  rawFilters,
  countryDetailsLookup,
  translate,
}: IBuildFilterCatalogueParams): IFilterData[] => {
  if (!rawFilters) {
    return [];
  }

  const formattedRequest = { ...CONNECTED_FILTERS, ...rawFilters };
  const allFilters: IFilterData[] = [];

  const getSearchValue = (values: Array<string | undefined>): string => values
    .filter((value): value is string => Boolean(value?.trim()))
    .join(' ');

  const getCountryFilterData = (filterValue: string): IFilterData => {
    const normalizedFilterValue = filterValue.trim();
    const country = getCountryDetails(normalizedFilterValue, countryDetailsLookup);
    const countryName = country?.name || normalizedFilterValue || filterValue;
    const countryCode = country?.iso2 || normalizedFilterValue || filterValue;

    return {
      name: countryName,
      uniqueIdentifier: countryCode,
      min: undefined,
      max: undefined,
      type: COUNTRY_CODES_FILTER_KEY,
      category: COUNTRY_CODES_FILTER_KEY,
      searchValue: getSearchValue([countryName, countryCode, country?.iso3, normalizedFilterValue]),
    };
  };

  const getFilterDisplayName = (filterType: string, filterValue: string) => {
    if (filterType === NAME_FILTERS.CONNECTION) {
      return translate(`common.${filterValue}`, filterValue);
    }

    return filterValue;
  };

  Object.keys(formattedRequest).forEach((key) => {
    const dataFilter = formattedRequest[key as keyof IBoardFilter];

    if (dataFilter && Array.isArray(dataFilter)) {
      const filterDataBody: IFilterData[] = dataFilter.map((item) => {
        if (isCountryCodeFilter(key)) {
          return getCountryFilterData(item);
        }

        return {
          name: getFilterDisplayName(key, item),
          uniqueIdentifier: item,
          min: undefined,
          max: undefined,
          type: key,
          category: key,
        };
      });
      allFilters.push(...filterDataBody);
    }

    if (dataFilter && key === NAME_FILTERS.REVENUE) {
      const filterItem = dataFilter as IBoardFilterItem;
      allFilters.push({
        name: key,
        uniqueIdentifier: undefined,
        min: filterItem.min,
        max: filterItem.max,
        type: key,
        category: key,
      });
    }

    if (dataFilter && key === NAME_FILTERS.UNIQUE_IDENTIFIER) {
      const uniqueIdentifierData = dataFilter as unknown as { [key: string]: IBoardFilterItem[] };
      Object.keys(uniqueIdentifierData).forEach((keyItem) => {
        const filterItem = uniqueIdentifierData[keyItem];
        if (filterItem && filterItem.length > 0) {
          const filterDataBody: IFilterData[] = filterItem.map((item) => ({
            name: item.name,
            uniqueIdentifier: getFilterIdentifier(item),
            min: undefined,
            max: undefined,
            type: keyItem,
            category: keyItem,
          }));
          allFilters.push(...filterDataBody);
        }
      });
    }
  });

  const uniqueFilters = allFilters.filter((filter, index, self) => {
    if (filter.type === NAME_FILTERS.REVENUE) {
      return index === self.findIndex((item) => item.type === filter.type);
    }

    return index === self.findIndex((item) => getFilterIdentifier(item) === getFilterIdentifier(filter)
      && item.type === filter.type);
  });

  return [...uniqueFilters].sort((leftFilter, rightFilter) => {
    if (isCountryCodeFilter(leftFilter.type) && isCountryCodeFilter(rightFilter.type)) {
      return leftFilter.name.localeCompare(rightFilter.name);
    }

    return 0;
  });
};

export default buildFilterCatalogue;
